import { makeAutoObservable, runInAction } from 'mobx';
import { makePersistable } from 'mobx-persist-store';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

class AuthStore {
  currentUser = null;
  userProfile = null;
  loading = true;
  unsubscribeAuth = null;

  constructor() {
    // makeAutoObservable automatically makes all properties observable
    // and all methods actions
    makeAutoObservable(this);

    // Automatically persist currentUser and userProfile to sessionStorage
    makePersistable(this, {
      name: 'AuthStore',
      properties: ['currentUser', 'userProfile'],
      storage: window.sessionStorage,
    });

    this.initAuth();
  }

  // Initialize Firebase auth listener
  initAuth() {
    // No need for manual cache loading - mobx-persist-store handles it automatically

    // Set up Firebase auth state listener
    this.unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Set auth user immediately - mobx-persist-store auto-saves to sessionStorage
          runInAction(() => {
            this.currentUser = user;
          });

          // Fetch user profile from Firestore
          const userDoc = await getDoc(doc(db, 'users', user.uid));

          if (userDoc.exists()) {
            const profileData = { id: user.uid, ...userDoc.data() };

            // mobx-persist-store automatically saves to sessionStorage
            runInAction(() => {
              this.userProfile = profileData;
              this.loading = false;
            });
          } else {
            runInAction(() => {
              this.userProfile = null;
              this.loading = false;
            });
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          runInAction(() => {
            this.userProfile = null;
            this.loading = false;
          });
        }
      } else {
        // User logged out - mobx-persist-store auto-clears sessionStorage
        runInAction(() => {
          this.currentUser = null;
          this.userProfile = null;
          this.loading = false;
        });
      }
    });
  }

  // Manually refresh user profile from Firestore
  async refreshUserProfile() {
    if (!this.currentUser) return null;

    try {
      const userDoc = await getDoc(doc(db, 'users', this.currentUser.uid));
      if (userDoc.exists()) {
        const profileData = { id: this.currentUser.uid, ...userDoc.data() };

        // mobx-persist-store automatically saves to sessionStorage
        runInAction(() => {
          this.userProfile = profileData;
        });

        return profileData;
      }
    } catch (error) {
      console.error('Error refreshing user profile:', error);
    }

    return null;
  }

  // Update user profile (for local updates)
  updateProfile(profileData) {
    // mobx-persist-store automatically saves to sessionStorage
    this.userProfile = profileData;
  }

  // Computed values
  get isAuthenticated() {
    return !!this.currentUser;
  }

  get userCountry() {
    return this.userProfile?.locationDetails?.country || null;
  }

  get userCountryCode() {
    return this.userProfile?.locationDetails?.countryCode || null;
  }

  get userName() {
    return this.userProfile?.name || this.currentUser?.displayName || '';
  }

  get userEmail() {
    return this.userProfile?.email || this.currentUser?.email || '';
  }

  get userMobile() {
    return this.userProfile?.mobile || '';
  }

  // Cleanup
  dispose() {
    if (this.unsubscribeAuth) {
      this.unsubscribeAuth();
    }
  }
}

// Create a singleton instance
const authStore = new AuthStore();

export default authStore;
